import { ChevronDown, ChevronUp, Eye, EyeOff, GripVertical } from "lucide-react"
import type { DragEvent } from "react"

export type Modulo = {
  id: number
  clave: string
  etiqueta: string
  ruta: string
  icono: string
  seccion: string
  orden: number
  activo: boolean
}

/**
 * Una entrada del menú: su nombre, su orden dentro de la sección y si se ofrece.
 *
 * ⚠ **Arrastrar y el selector «Sección» hacen lo MISMO, y los dos existen.** WCAG
 * 2.5.7 exige una alternativa de un solo puntero para toda operación de arrastre —
 * quien navega con teclado, con lector de pantalla o desde una tableta tiene que
 * poder mover un módulo igual—. Se construyeron a la vez a propósito: la alternativa
 * que se deja «para después» no llega nunca.
 *
 * ⚠ **El asa de arrastre es un adorno, no un control.** Va con `aria-hidden` porque
 * lo que se arrastra es la fila entera; anunciarla como botón prometería a un lector
 * de pantalla una acción que no puede hacer, y la que sí puede está en el selector.
 */

export function FilaModulo({
  modulo,
  primera,
  ultima,
  secciones,
  arrastrada,
  onSubir,
  onBajar,
  onSeccion,
  onAlternar,
  onArrastrar,
  onSoltarArrastre,
}: {
  modulo: Modulo
  primera: boolean
  ultima: boolean
  secciones: string[]
  arrastrada: boolean
  onSubir: () => void
  onBajar: () => void
  onSeccion: (seccion: string) => void
  onAlternar: () => void
  onArrastrar: (evento: DragEvent<HTMLLIElement>) => void
  onSoltarArrastre: () => void
}) {
  return (
    <li
      data-testid={`modulo-${modulo.clave}`}
      draggable
      onDragStart={onArrastrar}
      onDragEnd={onSoltarArrastre}
      className="flex flex-wrap items-center gap-3 px-3 py-2"
      style={{
        borderTop: primera ? undefined : "1px solid var(--border)",
        // Se atenúa la que viaja, para que se vea de dónde salió sin sacarla del
        // sitio: una fila que desaparece al empezar a arrastrar hace saltar la lista.
        opacity: arrastrada ? 0.4 : 1,
        cursor: "grab",
      }}
    >
      <GripVertical
        size={14}
        aria-hidden={true}
        style={{ color: "var(--fg-muted)", flexShrink: 0 }}
      />

      <span className="min-w-0 flex-1">
        <span
          className="block text-sm"
          // Lo apagado sigue legible, sólo atenuado: en gris del todo se lee como
          // deshabilitado y no como «no se ofrece».
          style={{ opacity: modulo.activo ? 1 : 0.55 }}
        >
          {modulo.etiqueta}
        </span>
        <span className="block font-mono text-[11px]" style={{ color: "var(--fg-muted)" }}>
          {modulo.ruta}
        </span>
      </span>

      <select
        value={modulo.seccion}
        onChange={(e) => onSeccion(e.target.value)}
        aria-label={`Sección de ${modulo.etiqueta}`}
        data-testid={`seccion-${modulo.clave}`}
        className="h-7 rounded-[var(--radius-control)] border px-1.5 text-xs"
        style={{
          borderColor: "var(--border)",
          backgroundColor: "var(--surface)",
          color: "var(--fg)",
        }}
      >
        {secciones.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>

      <button
        type="button"
        onClick={onSubir}
        disabled={primera}
        aria-label={`Subir ${modulo.etiqueta}`}
        className="boton-secundario h-7 w-7 justify-center p-0"
      >
        <ChevronUp size={14} aria-hidden={true} />
      </button>
      <button
        type="button"
        onClick={onBajar}
        disabled={ultima}
        aria-label={`Bajar ${modulo.etiqueta}`}
        className="boton-secundario h-7 w-7 justify-center p-0"
      >
        <ChevronDown size={14} aria-hidden={true} />
      </button>
      <button
        type="button"
        onClick={onAlternar}
        data-testid={`alternar-${modulo.clave}`}
        aria-pressed={modulo.activo}
        className="boton-secundario h-7 gap-1.5 px-2 text-xs"
        style={modulo.activo ? { color: "var(--primary-texto)" } : undefined}
      >
        {modulo.activo ? (
          <Eye size={14} aria-hidden={true} />
        ) : (
          <EyeOff size={14} aria-hidden={true} />
        )}
        {modulo.activo ? "En el menú" : "Fuera"}
      </button>
    </li>
  )
}
